import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import type { DeckSlide } from '../deckSlides'

const PAGE_WIDTH = 1440
const PAGE_HEIGHT = 810
export const STORYBOARD_FILE_NAME = 'Snakker-LEO-Pharma-Storyboard.pdf'
/** Long enough for each slide's entrance motion to settle before capture. */
const SETTLE_MS = 900

type ProgressHandler = (done: number, total: number) => void

/**
 * Renders every slide at full size off-screen, rasterises each one, and returns
 * the finished PDF as a Blob.
 *
 * Notes:
 * - `allowTaint` must stay false: tainting the canvas makes toDataURL throw.
 * - Returning a Blob (instead of calling pdf.save) lets the caller offer a real
 *   anchor click, which sandboxed previews allow where programmatic saves fail.
 */
export async function buildStoryboardPdf(
  pages: DeckSlide[],
  onProgress?: ProgressHandler,
): Promise<Blob> {
  if (typeof document === 'undefined') throw new Error('No document available')
  if (pages.length === 0) throw new Error('No slides to export')

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  Object.assign(host.style, {
    position: 'fixed',
    top: '0px',
    left: '-20000px',
    width: '100vw',
    height: '56.25vw',
    overflow: 'hidden',
    zIndex: '-1',
    pointerEvents: 'none',
    background: '#0C0C0C',
  })
  document.body.appendChild(host)

  const root = createRoot(host)
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
    compress: true,
  })

  try {
    if (document.fonts?.ready) await document.fonts.ready

    for (let index = 0; index < pages.length; index += 1) {
      root.render(createElement(pages[index].component))
      await waitFrames(3)
      await waitForImages(host)
      await delay(SETTLE_MS)

      const canvas = await html2canvas(host, {
        backgroundColor: '#0C0C0C',
        useCORS: true,
        allowTaint: false,
        imageTimeout: 6000,
        scale: 1.5,
        logging: false,
      })

      if (index > 0) pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT], 'landscape')
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.9),
        'JPEG',
        0,
        0,
        PAGE_WIDTH,
        PAGE_HEIGHT,
      )
      onProgress?.(index + 1, pages.length)
    }

    return pdf.output('blob')
  } finally {
    root.unmount()
    host.remove()
  }
}

/** Best-effort programmatic download; may be blocked in sandboxed previews. */
export function triggerDownload(url: string, fileName: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count
    const step = () => {
      remaining -= 1
      if (remaining <= 0) resolve()
      else window.requestAnimationFrame(step)
    }
    window.requestAnimationFrame(step)
  })
}

function waitForImages(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  return Promise.all(
    images.map((image) =>
      image.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            image.addEventListener('load', () => resolve(), { once: true })
            image.addEventListener('error', () => resolve(), { once: true })
          }),
    ),
  ).then(() => undefined)
}
